import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type GenerationStage = {
  id: string;
  label: string;
  progressRange: [number, number];
};

type GenerationLoadingStateProps = {
  title: string;
  estimatedDuration: string;
  currentStageId: string | null;
  stages: GenerationStage[];
  tips: string[];
};

function useAnimatedProgress(target: number) {
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const progressRef = useRef(0);

  useEffect(() => {
    const animate = () => {
      const current = progressRef.current;
      const distance = target - current;

      if (Math.abs(distance) < 0.1) {
        progressRef.current = target;
        setProgress(target);
        return;
      }

      const speed = distance > 20 ? 0.06 : 0.02;
      progressRef.current = current + distance * speed;
      setProgress(progressRef.current);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target]);

  return progress;
}

function getTargetProgress(
  stages: GenerationStage[],
  currentStageId: string | null,
  elapsedMs: number,
): number {
  if (!currentStageId) return 5;

  const stageIndex = stages.findIndex((s) => s.id === currentStageId);
  if (stageIndex === -1) return 5;

  const stage = stages[stageIndex];
  const [min, max] = stage.progressRange;

  const stageElapsed = Math.min(elapsedMs / 1000, 60);
  const asymptotic = 1 - 1 / (1 + stageElapsed * 0.04);
  return min + (max - min) * asymptotic;
}

export function GenerationLoadingState({
  title,
  estimatedDuration,
  currentStageId,
  stages,
  tips,
}: GenerationLoadingStateProps) {
  const [tipIndex, setTipIndex] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);
  const [elapsedMs, setElapsedMs] = useState(0);
  const stageStartTime = useRef(Date.now());
  const prevStageId = useRef(currentStageId);

  if (prevStageId.current !== currentStageId) {
    prevStageId.current = currentStageId;
    stageStartTime.current = Date.now();
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedMs(Date.now() - stageStartTime.current);
    }, 250);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (tips.length <= 1) return;

    const interval = setInterval(() => {
      setTipVisible(false);
      setTimeout(() => {
        setTipIndex((prev) => (prev + 1) % tips.length);
        setTipVisible(true);
      }, 400);
    }, 10_000);

    return () => clearInterval(interval);
  }, [tips.length]);

  const currentStageIndex = currentStageId
    ? stages.findIndex((s) => s.id === currentStageId)
    : -1;

  const targetProgress = getTargetProgress(stages, currentStageId, elapsedMs);
  const animatedProgress = useAnimatedProgress(targetProgress);

  const currentStageLabel =
    currentStageIndex >= 0
      ? stages[currentStageIndex].label
      : "Preparing...";

  return (
    <div className="mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center py-8 sm:py-10">
      {/* Icon with breathing glow */}
      <div className="relative mb-6 flex h-20 w-20 items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-primary/10 animate-breathe" />
        <div className="absolute inset-1 rounded-full bg-primary/5 animate-breathe-delayed" />
        <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
          <Sparkles className="h-6 w-6" />
        </div>
      </div>

      {/* Title */}
      <h3 className="mb-1 text-2xl font-semibold tracking-tight">{title}</h3>

      {/* Stage indicator */}
      <div className="mb-6 flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          {currentStageIndex >= 0
            ? `Step ${currentStageIndex + 1} of ${stages.length}`
            : "Starting"}
        </span>
        <span className="text-muted-foreground/40">·</span>
        <span className="text-sm text-muted-foreground">{currentStageLabel}</span>
      </div>

      {/* Progress bar */}
      <div className="mb-8 w-full max-w-sm space-y-2.5">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-none"
            style={{ width: `${animatedProgress}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground/70">
          <span>{Math.round(animatedProgress)}%</span>
          <span>{estimatedDuration}</span>
        </div>
      </div>

      {/* Stage pills */}
      <div className="mb-8 flex items-center gap-2">
        {stages.map((stage, index) => {
          const isActive = index === currentStageIndex;
          const isCompleted = currentStageIndex > index;
          return (
            <div key={stage.id} className="flex items-center gap-2">
              {index > 0 && (
                <div
                  className={cn(
                    "h-px w-6 transition-colors duration-500",
                    isCompleted ? "bg-primary" : "bg-border",
                  )}
                />
              )}
              <div
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-all duration-500",
                  isActive
                    ? "bg-primary/10 text-primary ring-1 ring-primary/20"
                    : isCompleted
                      ? "bg-primary/5 text-primary/70"
                      : "bg-muted text-muted-foreground/50",
                )}
              >
                {stage.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Educational tip card */}
      {tips.length > 0 && (
        <div className="w-full max-w-sm">
          <div className="rounded-lg border border-border/60 bg-muted/30 px-5 py-4">
            <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground/60">
              Did you know?
            </p>
            <p
              className={cn(
                "text-sm text-muted-foreground leading-relaxed transition-opacity duration-400",
                tipVisible ? "opacity-100" : "opacity-0",
              )}
            >
              {tips[tipIndex]}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
